/**
 * Option preview URL resolver for Content Studio option cards.
 *
 * Each option card (lighting, shadow, depth-of-field, framing, etc.)
 * can display a pre-generated realistic preview image so vendors see
 * what the option actually does before committing to it. Images are
 * produced offline by backoffice-worker/scripts/generate-option-previews.ts
 * and served from DigitalOcean Spaces with long-cache CDN headers.
 *
 * URL pattern:
 *   {CDN}/content-studio/option-previews/{inputKey}/{valueSlug}.webp
 *
 * Why an allow-list rather than always constructing a URL:
 *   - Not every option has a preview (some are abstract — aspectRatio,
 *     resolution, stylePrompt — and covered by the SVG illustrations).
 *   - Optimistically rendering an <img> for every option would cause
 *     visible 404 flashes on unmapped ones. The allow-list gates the
 *     <img> render so missing previews gracefully fall back to the
 *     existing illustration renderer.
 *
 * After running the generator script:
 *   1. The script prints an HAS_PREVIEW snippet covering everything
 *      it successfully uploaded.
 *   2. Paste that into HAS_PREVIEW below, commit, deploy.
 */

// Base URL of the Spaces bucket that holds option previews. Defaults
// to the production CDN; override via env for staging / local testing.
const PREVIEW_BASE_URL =
    (process.env.REACT_APP_CS_PREVIEW_BASE_URL
        || 'https://ard-pms-prod.fra1.digitaloceanspaces.com/content-studio/option-previews'
    ).replace(/\/+$/, '');

/**
 * Which (inputKey, value) pairs have a pre-generated preview image
 * available. Gates the <img> render in IllustratedOptionCard.
 *
 * Grows over time as the generator script is re-run. Keep entries
 * alphabetically ordered within each group for easy scanning.
 */
const HAS_PREVIEW: Record<string, Set<string>> = {
    // Populated by the generator scripts. Keep this file in sync after
    // each run (each script prints a ready-to-paste snippet).
    preset: new Set([
        // Furniture (model3d)
        'furniture-catalog-facing-white',
        'furniture-sunlit-empty-room',
        'furniture-amber-blinds',
        'furniture-palm-shadow-styled',
        'furniture-dark-moody-window',
        'furniture-arch-peek-bokeh',
        'furniture-overhead-hard-shadows',
        // Surface
        'travertine-two-tone',
        'dappled-marble-caustics',
        'water-surface-caustics',
        'linen-oatmeal-soft',
        'white-pedestal-leaning',
        'soft-grey-long-shadow',
        'warm-paper-gradient',
        'concrete-corner-window',
        'blush-pink-hard-sun',
    ]),
};

/**
 * Normalize an option value to a URL-safe filename slug.
 * Mirrors the generator's slugging so the URL resolves.
 *   "1:1"           → "1-1"
 *   "soft-diffuse"  → "soft-diffuse"
 *   "SoftDiffuse"   → "softdiffuse"
 */
function slug(value: string): string {
    return String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function getOptionPreviewUrl(inputKey: string, value: any): string | null {
    if (value === undefined || value === null || value === '') return null;
    const set = HAS_PREVIEW[inputKey];
    if (!set || !set.has(String(value))) return null;
    return `${PREVIEW_BASE_URL}/${inputKey}/${slug(String(value))}.webp`;
}

/**
 * Options whose preview is a short looping CLIP rather than a still.
 *
 * Camera moves are the case a still genuinely cannot express — "orbit"
 * and "dolly-in" are identical frozen. Each value below has a ~3s muted
 * mp4 (plus a .jpg poster for first paint) generated by
 * backoffice-worker/scripts/generate-camera-move-previews.ts from a
 * single shared subject still, so the ONLY thing varying between cards
 * is the camera motion itself.
 *
 * Same allow-list rationale as HAS_PREVIEW: unmapped values fall back to
 * the still/illustration path instead of flashing a 404.
 */
const HAS_VIDEO_PREVIEW: Record<string, Set<string>> = {
    // A value only belongs here once its clip actually conveys the move at
    // card size — an unconvincing preview is worse than none, since the
    // fallback text card at least doesn't mislead. Drop a value from this
    // list (the asset can stay on the CDN) if its clip stops reading.
    cameraMove: new Set([
        'orbit',
        'dolly-in',
        'dolly-out',
        'push-in',
        'pull-back',
        'pan-left',
        'pan-right',
        'tilt-up',
        'tilt-down',
        'crane-up',
        'crane-down',
        'parallax',
    ]),
};

/**
 * Folder of the current clip set per input. Clips are served `immutable`
 * for a year, so a regenerated set lives under a new folder instead of
 * overwriting — otherwise browsers that have seen the old cards keep them.
 * Must match SET_VERSION in generate-camera-move-previews.ts.
 */
const PREVIEW_VIDEO_VERSION: Record<string, string> = {
    // v2: MiniMax H3 Max on a neutral grey subject (v1 was LTX on beige).
    cameraMove: 'v2',
};

/** Looping preview clip for an option, or null when it has none. */
export function getOptionPreviewVideoUrl(inputKey: string, value: any): string | null {
    if (value === undefined || value === null || value === '') return null;
    const set = HAS_VIDEO_PREVIEW[inputKey];
    if (!set || !set.has(String(value))) return null;
    const version = PREVIEW_VIDEO_VERSION[inputKey];
    return `${PREVIEW_BASE_URL}/${inputKey}/${version ? `${version}/` : ''}${slug(String(value))}.mp4`;
}

/**
 * Poster frame for a preview clip — shown while the video buffers so the
 * card never renders as an empty box. Same basename, .jpg extension.
 */
export function getOptionPreviewPosterUrl(inputKey: string, value: any): string | null {
    const video = getOptionPreviewVideoUrl(inputKey, value);
    return video ? video.replace(/\.mp4$/, '.jpg') : null;
}
